import React, { FC, useEffect, useMemo } from 'react';
import { Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore, CardBrand } from 'src/store/subscriptionStore';
import {
  useSubscriptionStateQuery,
  useBillingProfileQuery,
  usePaymentMethodsQuery,
} from 'src/api/subscription/useSubscriptionApi';
import { ReactComponent as FreeTrialUnlockedSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/free-trial-unlocked/free-trial-unlocked.svg';
import { CardBrandLogo } from '../components/CardBrandLogo';
import './SubscriptionModals.scss';

interface PaymentConfirmedModalProps {
  isOpen: boolean;
  onClose: () => void;
  isTrialMode?: boolean;
  amountFormatted?: string | null;
  dueTodayFormatted?: string | null;
  firstPaymentFormatted?: string | null;
  trialEndsDateFormatted?: string | null;
  nextBillingDateFormatted?: string | null;
}

export const PaymentConfirmedModal: FC<PaymentConfirmedModalProps> = ({
  isOpen,
  onClose,
  isTrialMode = true,
  amountFormatted,
  dueTodayFormatted,
  firstPaymentFormatted,
  trialEndsDateFormatted,
  nextBillingDateFormatted,
}) => {
  const navigate = useNavigate();
  const { billingCycle, paymentMethods, billingInfo, removePromoCode } = useSubscriptionStore();
  const { data: serverState } = useSubscriptionStateQuery(isOpen);
  const { data: serverBillingProfile } = useBillingProfileQuery(isOpen);
  const { data: serverPaymentMethods } = usePaymentMethodsQuery(isOpen);

  useEffect(() => {
    if (isOpen) {
      removePromoCode();
    }
  }, [isOpen, removePromoCode]);

  const serverCards = serverPaymentMethods?.payment_methods || [];
  const defaultMethodCard =
    serverCards.find((c: any) => c.id === serverPaymentMethods?.default_payment_method_id) ||
    serverCards.find((c: any) => c.is_default || c.isDefault) ||
    serverCards[0];

  const defaultCard =
    (serverState?.default_payment_method
      ? {
          brand: serverState.default_payment_method.brand.toLowerCase() as CardBrand,
          last4: serverState.default_payment_method.last4,
        }
      : null) ||
    (defaultMethodCard
      ? {
          brand: (defaultMethodCard.brand?.toLowerCase() || 'visa') as CardBrand,
          last4: defaultMethodCard.last4,
        }
      : null) ||
    paymentMethods.find((c) => c.isDefault) ||
    paymentMethods[0];

  const receiptEmail =
    serverBillingProfile?.billingEmail || billingInfo.billingEmail || 'your email';

  const handleStartPro = () => {
    removePromoCode();
    onClose();
    navigate('/onboarding-trainer/dashboard/program-generator/ai');
  };

  const handleViewSubscription = () => {
    removePromoCode();
    onClose();
    navigate('/onboarding-trainer/dashboard/subscription/my-subscription', { replace: true });
  };

  const nextBillingDate = useMemo(() => {
    if (nextBillingDateFormatted && nextBillingDateFormatted !== '—') {
      return nextBillingDateFormatted;
    }
    if (serverState?.current_period_end) {
      try {
        const d = new Date(serverState.current_period_end);
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          });
        }
      } catch {
        // Fallback
      }
    }
    const cycle =
      serverState?.billing_interval === 'year' || billingCycle === 'yearly' ? 'year' : 'month';
    const d = new Date();
    if (cycle === 'year') {
      d.setFullYear(d.getFullYear() + 1);
    } else {
      d.setMonth(d.getMonth() + 1);
    }
    return d.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }, [nextBillingDateFormatted, serverState, billingCycle]);

  const resolvedDate = isTrialMode
    ? trialEndsDateFormatted && trialEndsDateFormatted !== '—'
      ? trialEndsDateFormatted
      : nextBillingDate
    : nextBillingDate;

  const amountDisplay = amountFormatted || serverState?.amount?.formatted || '—';

  const dueTodayDisplay = dueTodayFormatted || (isTrialMode ? 'Free' : amountDisplay);

  const firstPaymentDisplay = firstPaymentFormatted || amountDisplay;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      centered
      width={480}
      closeIcon={null}
      rootClassName="subscription-modal-wrapper"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.75)',
        },
      }}
    >
      <div className="result-modal-content">
        <div className="result-icon-wrapper">
          <FreeTrialUnlockedSVG />
        </div>

        <h2>{isTrialMode ? 'Free Trial Unlocked!' : "You're now on Pro!"}</h2>
        <p className="result-subtitle-1">
          {isTrialMode
            ? 'Your 14-day Pro trial is now active.'
            : 'Thank you! Your payment was successful.'}
        </p>
        <p className="result-subtitle-2">{'You now have access to all Pro features.'}</p>

        {/* Breakdown Card */}
        <div className="result-details-box">
          <div className="detail-row">
            <span className="detail-label">{'Plan'}</span>
            <span className="detail-value">{'Pro Plan'}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{'Billing Cycle'}</span>
            <span className="detail-value" style={{ textTransform: 'capitalize' }}>
              {billingCycle}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{isTrialMode ? 'Due Today' : 'Amount'}</span>
            <span className="detail-value">{isTrialMode ? dueTodayDisplay : amountDisplay}</span>
          </div>
          <div className="detail-row">
            <span className="detail-label">
              {isTrialMode ? 'First Payment' : 'Next Billing Date'}
            </span>
            <span className="detail-value">
              {isTrialMode ? `${firstPaymentDisplay} on ${resolvedDate}` : resolvedDate}
            </span>
          </div>
          <div className="detail-row">
            <span className="detail-label">{'Payment Method'}</span>
            <span className="detail-value">
              {defaultCard ? (
                <>
                  <CardBrandLogo brand={defaultCard.brand} style={{ width: 34, height: 20 }} />
                  <span>{`•••• ${defaultCard.last4}`}</span>
                </>
              ) : (
                <span>{'—'}</span>
              )}
            </span>
          </div>
        </div>

        <p className="result-receipt-note">
          {isTrialMode
            ? 'Your first payment will be collected after the trial ends unless you cancel before then.'
            : `A receipt has been sent to ${receiptEmail}`}
        </p>

        <div className="result-actions">
          <button type="button" className="btn-primary-action" onClick={handleStartPro}>
            {'Start Using Pro Features'}
          </button>
          <button type="button" className="btn-secondary-action" onClick={handleViewSubscription}>
            {'View Subscription'}
          </button>
        </div>
      </div>
    </Modal>
  );
};
